import { useMemo, useState } from "react";
import { useQuery } from "react-query";
import { useIntl } from "react-intl";
import {
  Box,
  List,
  ListItem,
  Popover,
  Skeleton,
  styled,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";

import { checkErrors, getDefaultOptions } from "../services/Category";
import { GenericObject } from "../services/Price";

export const getBatchInfos = async () => {
  const url = new URL("/api/batch/status/", window.origin);

  const response = await fetch(url.toString(), getDefaultOptions());

  checkErrors(response);

  return response.json();
};

const StyledBox = styled(Box, {
  shouldForwardProp: (prop) => prop !== "status",
})<{ status: "positive" | "negative" | "warning" }>(({ theme, status }) => ({
  alignItems: "center",
  backgroundColor:
    status === "positive"
      ? theme.vars.palette.status.success
      : status === "warning"
      ? theme.vars.palette.warning.main
      : theme.vars.palette.error.main,
  borderRadius: "6px",
  color:
    status === "positive"
      ? theme.vars.palette.text.primary
      : status === "warning"
      ? theme.vars.palette.warning.contrastText
      : theme.vars.palette.error.contrastText,
  cursor: "pointer",
  display: "flex",
  gap: theme.spacing(1),
  paddingTop: 0,
  paddingRight: theme.spacing(0.25),
  paddingBottom: 0,
  paddingLeft: theme.spacing(1),
  position: "relative",
  width: "fit-content",
}));

const DashboardBatch = () => {
  const intl = useIntl();

  const batchInfos = useQuery(["batch-infos"], () => getBatchInfos());

  const [anchorEl, setAnchorEl] = useState<
    (EventTarget & HTMLDivElement) | null
  >(null);
  const status: "positive" | "negative" | "warning" = useMemo(() => {
    if (!batchInfos.data) return "negative";
    const batchStatus =
      batchInfos.data.is_batch_running || !batchInfos.data.has_batch_run_today
        ? "negative"
        : "positive";

    const bookingStatus = batchInfos.data.were_bookings_made_yesterday
      ? "positive"
      : "negative";

    if (batchStatus === bookingStatus && batchStatus === "negative") {
      return "negative";
    } else if (batchStatus === bookingStatus && batchStatus === "positive") {
      return "positive";
    } else {
      return "warning";
    }
  }, [batchInfos.data]);

  const handlePopoverOpen = (event: React.MouseEvent<HTMLDivElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? "mouse-over-popover" : undefined;

  return (
    <Box>
      {batchInfos.isLoading && <Skeleton width={220} height={40} />}
      {batchInfos.data && (
        <>
          <StyledBox
            status={status}
            aria-owns={id}
            aria-haspopup="true"
            onClick={handlePopoverOpen}
          >
            <Typography component="p" sx={{ fontSize: "0.8125rem" }}>
              {intl.formatMessage({ id: "BATCH_BADGE_TITLE" })}
            </Typography>
            {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </StyledBox>
          <Popover
            id={id}
            open={open}
            anchorEl={anchorEl}
            disableAutoFocus
            disableEnforceFocus
            onClose={handlePopoverClose}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "right",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
          >
            <Box
              sx={{
                padding: 1,
              }}
            >
              <Typography
                component="p"
                sx={{
                  fontSize: "0.8125rem",
                }}
              >
                {batchInfos.data.is_batch_running ||
                !batchInfos.data.has_batch_run_today ? (
                  <HighlightOffIcon
                    color="error"
                    fontSize="small"
                    sx={{ verticalAlign: "bottom", mr: 0.75 }}
                  />
                ) : (
                  <CheckCircleOutlinedIcon
                    color="success"
                    fontSize="small"
                    sx={{ verticalAlign: "bottom", mr: 0.75 }}
                  />
                )}
                <Typography
                  component="span"
                  sx={{
                    fontSize: "0.8125rem",
                    fontWeight: "bold",
                  }}
                >
                  {intl.formatMessage({ id: "BATCH_MESSAGE" })}{" "}
                </Typography>
                {batchInfos.data.batch_message}
              </Typography>

              <Typography
                component="p"
                sx={{
                  fontSize: "0.8125rem",
                  mt: 1,
                }}
              >
                {batchInfos.data.were_bookings_made_yesterday ? (
                  <CheckCircleOutlinedIcon
                    color="success"
                    fontSize="small"
                    sx={{ verticalAlign: "bottom", mr: 0.75 }}
                  />
                ) : (
                  <HighlightOffIcon
                    color="error"
                    fontSize="small"
                    sx={{ verticalAlign: "bottom", mr: 0.75 }}
                  />
                )}
                <Typography
                  component="span"
                  sx={{
                    fontSize: "0.8125rem",
                    fontWeight: "bold",
                  }}
                >
                  {intl.formatMessage({ id: "BATCH_BOOKING_MESSAGE" })}{" "}
                </Typography>
                {batchInfos.data.reservation_message
                  ? batchInfos.data.reservation_message
                  : intl.formatMessage({ id: "NO_DATA" })}
              </Typography>

              {batchInfos.data.last_reservations_by_pms &&
                batchInfos.data.last_reservations_by_pms.length > 0 && (
                  <>
                    <Typography
                      component="p"
                      sx={{ fontSize: "0.8125rem", mt: 1, fontWeight: "bold" }}
                    >
                      {intl.formatMessage({ id: "BATCH_LAST_BOOKING" })}
                    </Typography>
                    <List dense disablePadding sx={{ listStyleType: "disc" }}>
                      {batchInfos.data.last_reservations_by_pms.map(
                        (pms: GenericObject, index: number) => (
                          <ListItem
                            key={index}
                            dense
                            disablePadding
                            sx={{
                              display: "list-item",
                              listStyle: "inside",
                            }}
                          >
                            {pms.is_pms_actualized === 1 ? (
                              <CheckCircleOutlinedIcon
                                color="success"
                                fontSize="small"
                                sx={{
                                  verticalAlign: "bottom",
                                  mr: 0.75,
                                }}
                              />
                            ) : (
                              <HighlightOffIcon
                                color="error"
                                fontSize="small"
                                sx={{
                                  verticalAlign: "bottom",
                                  mr: 0.75,
                                }}
                              />
                            )}
                            <Typography
                              component={"span"}
                              sx={{
                                fontSize: "0.8125rem",
                                fontWeight: "bold",
                              }}
                            >
                              {pms.pms_name}
                            </Typography>
                            {" - "}
                            <Typography
                              component={"span"}
                              sx={{ fontSize: "0.8125rem" }}
                            >
                              {pms.last_reservation_date}
                            </Typography>
                          </ListItem>
                        )
                      )}
                    </List>
                  </>
                )}
            </Box>
          </Popover>
        </>
      )}
    </Box>
  );
};

export default DashboardBatch;
